import { DocumentNode } from 'graphql';
import { ShoppingBag } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetProductsQuery as GetProducts } from '__generated__/graphql';

import { DragDropList } from '../../../../common/Common/DragDrop';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import GetProductsQuery from './GetProductsQuery';
import SortProductsMutation from './SortProductsMutation';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';

type Event = GetProducts['project']['event'];
type Product = Event['products'][0];

interface ProductSorterProps {
  event: Event;
  ticketUpgrades: Product[];
  standAloneProducts: Product[];
  onSuccess?: () => void;
  refetchQueries?: DocumentNode[];
}

const ProductSorter = ({
  event, ticketUpgrades, standAloneProducts, onSuccess, refetchQueries = [GetProductsQuery],
}: ProductSorterProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      ticket_upgrades: ticketUpgrades.map((product) => ({
        id: product.id,
        title: product.title,
      })),
      stand_alone_products: standAloneProducts.map((product) => ({
        id: product.id,
        title: product.title,
      })),
    },
  });

  const [sortProducts, { loading }] = useMutation(
    SortProductsMutation,
    {
      variables: {
        input: {
          products: [...form.values.ticket_upgrades, ...form.values.stand_alone_products].map((product) => ({
            id: product.id,
          })),
        },
      },
      onCompleted: () => {
        notifier.success(t('notifications.products_sorted'));

        onSuccess?.();
      },
      onError: () => {
        notifier.error(t('notifications.something_went_wrong'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  return (
    <UI.Form onSubmit={() => sortProducts()}>
      <UI.GridContainer>
        <UI.SectionHeading>
          {t('sort_products')}
        </UI.SectionHeading>

        {ticketUpgrades.length > 0 && (
          <>
            <UI.HR />

            <UI.H4 sc={{ strong: true, py: 0.75 }}>
              <UI.Icon sc={{ mr: 1.5 }}>
                <UI.Icons.Ticket />
              </UI.Icon>
              {t('ticket_upgrades')}
            </UI.H4>

            <DragDropList
              items={form.values.ticket_upgrades}
              onChange={(items) => form.set('ticket_upgrades', items)}
            />
          </>
        )}

        {standAloneProducts.length > 0 && (
          <>
            <UI.HR />

            <UI.H4 sc={{ strong: true, py: 0.75 }}>
              <UI.Icon sc={{ mr: 1.5 }}>
                <ShoppingBag />
              </UI.Icon>
              {t('stand_alone_products')}
            </UI.H4>

            <DragDropList
              items={form.values.stand_alone_products}
              onChange={(items) => form.set('stand_alone_products', items)}
            />
          </>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save')}
          closeTo={`/${project.id}/events/${event.id}/products`}
          closeLabel={t('cancel')}
          loading={loading}
        />
      </UI.GridContainer>
    </UI.Form>
  );
};

export default ProductSorter;
